import React, { useState } from "react";

// ── Shared note-section formatting ──────────────────────────────────────────
// The note model emits P/E and ROS as run-on prose ("General Appearance:
// well-nourished… Head: normocephalic… Eyes: …") and A&P entries as
// "Diagnosis (ICD10):\nplan text". These helpers break that into readable,
// bold-labelled lines for every screen that READS a note (LiveScribePanel,
// the CareIQ visit modal, Note Review). Editors (textareas) are unaffected.

// Break run-on text before an inline clinical heading like "Respiratory:" or
// "Psychiatric/Neurological:" when it follows a sentence boundary. The label
// char class includes "." so ICD-coded headings ("Gout (M10.9):") match too.
const INLINE_LABEL_BREAK = /(?<=[.;])\s+(?=[A-Z][A-Za-z0-9/&'().,+ -]{0,60}:(?:\s|$))/g;
// A short leading "Label:" at the start of a line (bolded when rendering).
const LEADING_LABEL = /^([A-Z][A-Za-z0-9/&'().,+ -]{0,60}?):\s*/;
// "Diagnosis Name (ICD10): plan…" on a single line — split heading from body.
const SINGLE_LINE_HEADING = /^([^:\n]{3,80}):\s+/;
// "Diagnosis Name (E11.9): plan…" — capture name, ICD-10 code, and plan.
const AP_ENTRY = /^\s*(.+?)\s*\(\s*([A-TV-Z][0-9][0-9A-Z]{0,2}(?:\.[0-9A-Z]{1,4})?)\s*\)\s*:?\s*([\s\S]*)$/;

// Parse one A&P entry into {name, icd, plan}; icd is null when uncoded.
export const parseAPEntry = (text) => {
    const t = typeof text === "string" ? text : String(text ?? "");
    const m = t.match(AP_ENTRY);
    if (m) return { name: m[1], icd: m[2], plan: m[3].trim() };
    const h = t.match(SINGLE_LINE_HEADING);
    if (h) return { name: h[1], icd: null, plan: t.slice(h[0].length).trim() };
    const lines = t.split("\n").map((l) => l.trim()).filter(Boolean);
    return { name: lines[0] || "", icd: null, plan: lines.slice(1).join("\n") };
};

// Pastel badge palette keyed by ICD-10 chapter letter.
const ICD_COLORS = {
    A: ["#FEE2E2", "#B91C1C"], B: ["#FEE2E2", "#B91C1C"], C: ["#F3E8FF", "#7C3AED"],
    D: ["#F3E8FF", "#7C3AED"], E: ["#D1FAE5", "#047857"], F: ["#EDE9FE", "#6D28D9"],
    G: ["#E0E7FF", "#4338CA"], H: ["#E0F2FE", "#0369A1"], I: ["#FFE4E6", "#BE123C"],
    J: ["#CFFAFE", "#0E7490"], K: ["#FEF3C7", "#B45309"], L: ["#ECFCCB", "#4D7C0F"],
    M: ["#FFEDD5", "#C2410C"], N: ["#CCFBF1", "#0F766E"], O: ["#FCE7F3", "#BE185D"],
    P: ["#FCE7F3", "#BE185D"], Q: ["#F3E8FF", "#7C3AED"], R: ["#F1F5F9", "#475569"],
    S: ["#FEE2E2", "#B91C1C"], T: ["#FEE2E2", "#B91C1C"], Z: ["#E2E8F0", "#334155"],
};

export const IcdBadge = ({ code }) => {
    if (!code) return null;
    const [bg, fg] = ICD_COLORS[code[0]] || ["#F1F5F9", "#475569"];
    return (
        <span
            style={{
                background: bg, color: fg, borderRadius: "999px", padding: "1px 9px",
                fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em",
                whiteSpace: "nowrap", verticalAlign: "middle",
            }}
            title={`ICD-10 ${code}`}
        >
            {code}
        </span>
    );
};

export const splitNoteLines = (text) =>
    typeof text === "string"
        ? text
              .replace(INLINE_LABEL_BREAK, "\n")
              .split("\n")
              .map((l) => l.trim())
              .filter(Boolean)
        : [];

// HTML variant for HTML-capable surfaces (the edit screen's ReactQuill modal
// and its dangerouslySetInnerHTML section previews): plain run-on prose becomes
// <strong>Label:</strong> lines joined with <br />. Content that already
// contains HTML (a previously Quill-edited note) passes through untouched.
export const noteTextToHtml = (text) => {
    if (typeof text !== "string" || !text.trim()) return "";
    if (/<[a-z][^>]*>/i.test(text)) return text; // already HTML — leave as-is
    const escaped = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    return splitNoteLines(escaped)
        .map((line) => {
            const m = line.match(LEADING_LABEL);
            return m ? `<strong>${m[1]}:</strong> ${line.slice(m[0].length)}` : line;
        })
        .join("<br />");
};

// Section text with each "Label:" finding on its own line, label bolded.
export const FormattedNoteText = ({ text }) => (
    <>
        {splitNoteLines(text).map((line, i) => {
            const m = line.match(LEADING_LABEL);
            return (
                <div key={i} style={{ marginBottom: "3px" }}>
                    {m ? (
                        <>
                            <strong>{m[1]}:</strong> {line.slice(m[0].length)}
                        </>
                    ) : (
                        line
                    )}
                </div>
            );
        })}
    </>
);

// Array section. numbered (A&P): each entry gets a bolded heading line
// (diagnosis + ICD) with its plan text as an indented paragraph. Non-numbered
// (meds, instructions): bulleted lines.
export const FormattedNoteList = ({ items, numbered = false }) => (
    <>
        {(items || []).map((item, i) => {
            const text = typeof item === "string" ? item : String(item ?? "");
            let lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
            let heading = lines[0] || "";
            let body = lines.slice(1).join("\n");
            // Single-line "Diagnosis (ICD): plan…" — split at the first colon.
            if (numbered && !body) {
                const m = heading.match(SINGLE_LINE_HEADING);
                if (m) {
                    body = heading.slice(m[0].length);
                    heading = m[1];
                }
            }
            if (!numbered) {
                return (
                    <div key={i} style={{ marginBottom: "3px" }}>
                        • {heading}
                        {body && (
                            <div style={{ paddingLeft: "12px" }}>
                                <FormattedNoteText text={body} />
                            </div>
                        )}
                    </div>
                );
            }
            const dx = parseAPEntry(text);
            return (
                <div key={i} style={{ marginBottom: "8px" }}>
                    <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        <span>{i + 1}. {dx.name.replace(/:$/, "")}</span>
                        <IcdBadge code={dx.icd} />
                    </div>
                    {dx.plan && (
                        <div style={{ paddingLeft: "14px" }}>
                            <FormattedNoteText text={dx.plan} />
                        </div>
                    )}
                </div>
            );
        })}
    </>
);

// ── Interactive A&P diagnosis list (edit screen) ────────────────────────────
// Each diagnosis renders as a card: bold name + chapter-colored ICD badge and,
// when onRemove is provided, an "×" that asks for confirmation inline before
// removing that diagnosis. Removal is staged by the caller (Save persists).
export const APDiagnosisList = ({ entries, onRemove }) => {
    const [confirmIdx, setConfirmIdx] = useState(null);
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
            {(entries || []).map((entry, i) => {
                const dx = parseAPEntry(entry);
                const confirming = confirmIdx === i;
                return (
                    <div
                        key={`${dx.icd || dx.name}-${i}`}
                        style={{
                            border: "1px solid #E5E7EB", borderLeft: "4px solid #E17954",
                            borderRadius: "8px", padding: "8px 12px", background: "#FFFFFF",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 700, color: "#1D2951", fontSize: "14px" }}>{dx.name.replace(/:$/, "")}</span>
                            <IcdBadge code={dx.icd} />
                            {onRemove && (
                                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
                                    {confirming ? (
                                        <>
                                            <span style={{ fontSize: "12px", color: "#B91C1C", fontWeight: 600 }}>Remove this diagnosis?</span>
                                            <button
                                                onClick={() => { setConfirmIdx(null); onRemove(i); }}
                                                style={{ fontSize: "12px", fontWeight: 700, color: "#FFF", background: "#B91C1C", border: "none", borderRadius: "6px", padding: "2px 10px", cursor: "pointer" }}
                                            >
                                                Remove
                                            </button>
                                            <button
                                                onClick={() => setConfirmIdx(null)}
                                                style={{ fontSize: "12px", fontWeight: 600, color: "#374151", background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: "6px", padding: "2px 10px", cursor: "pointer" }}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setConfirmIdx(i)}
                                            title="Remove diagnosis"
                                            style={{ fontSize: "14px", lineHeight: 1, color: "#9CA3AF", background: "transparent", border: "1px solid #E5E7EB", borderRadius: "999px", width: "22px", height: "22px", cursor: "pointer" }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </span>
                            )}
                        </div>
                        {dx.plan && (
                            <div style={{ marginTop: "4px", color: "#374151", fontSize: "13px", lineHeight: 1.55 }}>
                                <FormattedNoteText text={dx.plan} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
